/**
 * /tenants/$slug/security/workstations — the workstations console.
 *
 * Thin shim: validate search, prefetch the roster, mount the feature page.
 *
 * A SECOND LEAF UNDER ACCESS, beside Windows sign-in rather than inside it (the fork recorded in
 * docs/product-areas.md by the product owner). Windows
 * sign-in answers "how does a person reach a machine" and gates on `auth_methods.read`; this page
 * answers "what is this machine, and what policy is it being handed" and gates on
 * `workstations.read`. Folding them into one page would have given that page two permissions and
 * ended the property its own comment claims — that one page answers both questions it exists for.
 *
 * The loader's prefetch deliberately swallows a rejection: an operator whose roster read fails — a
 * 403 from a role that lost the permission, a 503 from the plane — belongs on the page with its
 * banner and retry button, not on the router's error screen with no way back.
 */

import { createFileRoute } from "@tanstack/react-router";
import { fetchWorkstations } from "@/features/workstations/queries";
import {
  workstationsSearchSchema,
  type WorkstationsSearch,
} from "@/features/workstations/schemas";
import { WorkstationsPage } from "@/features/workstations/pages/workstations-page";
import { queryKeys } from "@/lib/query-keys";

export const Route = createFileRoute("/_authed/tenants/$slug/security/workstations")({
  validateSearch: (raw: Record<string, unknown>): WorkstationsSearch =>
    workstationsSearchSchema.parse(raw),
  loader: ({ context }) =>
    context.queryClient
      .ensureQueryData({
        queryKey: queryKeys.workstations.list(context.tenant.id),
        queryFn: () => fetchWorkstations(context.tenant.id),
      })
      .catch(() => undefined),
  component: WorkstationsPage,
});
